import { ArpFields, ArpsDesignerKind } from "../../../constants/forecasts.constants";
import { ArpSegment, ForecastConstant } from "../../../models/UserArpsModel";
import { ICheckedForecast } from "../../../store/features";
import { getDefaultRateUnitsFromString } from "../../../utils/arps/getDefaultProductUnits";
import { CumulativeData } from "../../forecasting/Forecasting";
import { getPrimaryProductLastValidSegmentEndDate } from "./arpsUtils";
import { addAvgMonthsToDate, avgMonthsDiff, daysDiff } from "./dates";

export let arpsWasm: typeof import("wasm/arps") = undefined;
import("wasm/arps.js").then((x) => {
  arpsWasm = x;
});

const RATIO_DECLINE_B_FACTOR = -1.0;
// Epsilon guard: prevents division by zero in WASM when Q0=0 (same pattern as ramp-up segments)
const EPSILON = 1e-7;
const RATIO_ZERO_GUARD = 0.000001;

/**
 * Interface for ratio decline display values
 */
export interface RatioDeclineValues {
  qo: number | undefined; // Start ratio value
  qi: number | undefined; // End ratio value
  duration: number | undefined; // Duration in months
}

/**
 * Get ratio display values from either constants or segments
 */
export function getRatioDisplayValues(
  arps: ICheckedForecast,
  ratioProduct: string
): RatioDeclineValues {
  // Check constants first (flat mode)
  const constant = arps.constants?.find((c) => c.product === ratioProduct);
  if (constant) {
    return {
      qo: constant.value,
      qi: constant.qi, // Read from constant's qi field
      duration: constant.duration // Read from constant's duration field
    };
  }

  // User-created simple decline: exactly 2 flagged segments (linear decline + flat).
  // Duration is the decline period only (segment 1), not the trailing flat segment.
  const flaggedSegments = arps.arps
    ?.filter((seg) => seg.product === ratioProduct && seg.isUserRatioDecline === true)
    ?.sort((a, b) => a.segmentIndex - b.segmentIndex);

  if (flaggedSegments && flaggedSegments.length === 2) {
    const [seg1] = flaggedSegments;
    const durationMonths = avgMonthsDiff(seg1.startDate, seg1.endDate);

    return {
      qo: seg1.qi, // Start rate from segment 1
      qi: seg1.qf, // End rate from segment 1 (= seg2.qi = seg2.qf)
      duration: durationMonths
    };
  }

  // EVA-6256: imported ratio Arps (any segment count, not flagged). Shown read-only as a
  // Q0/Duration/Qi summary: start rate of the first segment, end rate of the last, and the
  // total span across segments.
  const importedSegments = arps.arps
    ?.filter((seg) => seg.product === ratioProduct && seg.isUserRatioDecline !== true)
    ?.sort((a, b) => a.segmentIndex - b.segmentIndex);

  if (importedSegments && importedSegments.length > 0) {
    const first = importedSegments[0];
    const last = importedSegments[importedSegments.length - 1];
    const durationMonths = importedSegments.reduce(
      (sum, seg) => sum + avgMonthsDiff(seg.startDate, seg.endDate),
      0
    );

    return {
      qo: first.qi,
      qi: last.qf,
      duration: durationMonths
    };
  }

  return { qo: undefined, qi: undefined, duration: undefined };
}

/**
 * Get primary product start and end dates
 */
function getPrimaryProductDates(
  arps: ICheckedForecast,
  kind: ArpsDesignerKind,
  cumulativeData: CumulativeData
): {
  startDate: string | undefined;
  endDate: string | undefined;
} {
  // For Type Wells: Primary product is the FIRST product in segments array
  const primaryProductSegments = arps.arps;
  if (!primaryProductSegments || primaryProductSegments.length === 0) {
    return { startDate: undefined, endDate: undefined };
  }

  const firstSegment = primaryProductSegments[0];
  const primaryProduct = firstSegment?.product;

  // Get all segments for the primary product
  const primarySegments = primaryProductSegments.filter(
    (seg) => seg.product === primaryProduct
  );

  // Get start date from first segment
  const startDate = primarySegments[0]?.startDate;

  // Get end date from last VALID segment (excluding segments with errors)
  const endDate = getPrimaryProductLastValidSegmentEndDate({
    segments: arps.arps,
    kind,
    cumulativeData
  })?.toISOString();

  return { startDate, endDate };
}

/**
 * Convert constant to segments (when user fills Qi and Duration)
 */
export function convertConstantToSegments(
  arps: ICheckedForecast,
  ratioProduct: string,
  qoValue: number,
  qiValue: number,
  durationMonths: number,
  kind: ArpsDesignerKind,
  cumulativeData: CumulativeData
): ICheckedForecast {
  // 1. Remove constant
  const updatedConstants = arps.constants.filter(
    (c) => !(c.product === ratioProduct && c.uniqueId === arps.uniqueID)
  );

  // 2. Get primary product dates
  const { startDate: primaryStartDate, endDate: primaryEndDate } = getPrimaryProductDates(
    arps,
    kind,
    cumulativeData
  );

  if (!primaryStartDate || !primaryEndDate) {
    // Can't create segments without primary product dates
    return arps;
  }

  // 3. Calculate dates
  const seg1StartDate = new Date(primaryStartDate);
  const seg1EndDate = addAvgMonthsToDate(seg1StartDate, durationMonths).toDate();

  // 4. Calculate Di
  if (!arpsWasm) {
    throw new Error("arpsWasm is undefined - cannot calculate decline rate");
  }

  const startDate = seg1StartDate.toISOString();
  const endDate = seg1EndDate.toISOString();

  const days = daysDiff(startDate, endDate);

  // Guard against division by zero in WASM when Q0=0 (same pattern as ramp-up segments)
  const qoForCalc = qoValue < EPSILON ? RATIO_ZERO_GUARD : qoValue;
  // Guard Qi=0 so the flat segment after the decline plots at ~0 rather than disappearing
  const qiForCalc = qiValue < EPSILON ? RATIO_ZERO_GUARD : qiValue;

  const calculatedDi = arpsWasm.calculateDiAtDaysFromQiToQf(
    qoForCalc,
    qiForCalc,
    RATIO_DECLINE_B_FACTOR,
    days
  );

  // 5. Create segments
  const segment1: ArpSegment = {
    product: ratioProduct,
    segmentIndex: 1,
    startDate: startDate,
    endDate: endDate,
    qi: qoForCalc, // Store guard value so chart rate formula (rate = qi * f(t)) works near 0
    qf: qiForCalc,
    di: calculatedDi,
    b: RATIO_DECLINE_B_FACTOR,
    trendCum: 0,
    df: 0,
    maxRate: 0,
    isUserRatioDecline: true
  };

  const segment2: ArpSegment = {
    product: ratioProduct,
    segmentIndex: 2,
    startDate: seg1EndDate.toISOString(),
    endDate: primaryEndDate,
    qi: qiForCalc,
    qf: qiForCalc,
    di: 0,
    b: 0,
    trendCum: 0,
    df: 0,
    maxRate: 0,
    isUserRatioDecline: true
  };

  // 6. Add segments to arps array
  const updatedArps = [...arps.arps, segment1, segment2];

  return {
    ...arps,
    constants: updatedConstants,
    arps: updatedArps
  };
}

/**
 * Convert segments to constant (when user clears Qi or Duration)
 */
export function convertSegmentsToConstant(
  arps: ICheckedForecast,
  ratioProduct: string,
  qoValue: number
): ICheckedForecast {
  // 1. Remove segments with isUserRatioDecline flag
  const updatedArps = arps.arps.filter(
    (seg) => !(seg.product === ratioProduct && seg.isUserRatioDecline === true)
  );

  // 2. Create constant - only set value (Q0), don't set qi/duration
  // When qi/duration are cleared, we want a "flat" constant without those fields
  const newConstant: ForecastConstant = {
    product: ratioProduct,
    uniqueId: arps.uniqueID,
    value: qoValue,
    unit: getDefaultRateUnitsFromString(ratioProduct)
  };

  // 3. Add to constants array (check for duplicates first)
  const existingConstantIndex = arps.constants.findIndex(
    (c) => c.product === ratioProduct && c.uniqueId === arps.uniqueID
  );

  const updatedConstants = [...arps.constants];
  if (existingConstantIndex >= 0) {
    updatedConstants[existingConstantIndex] = newConstant;
  } else {
    updatedConstants.push(newConstant);
  }

  return {
    ...arps,
    constants: updatedConstants,
    arps: updatedArps
  };
}

/**
 * Update function for ratio decline segments
 * Handles changes to Q0, Duration, and Qi fields by recalculating the segments
 */
export function updateRatioDeclineSegments({
  productArps,
  header,
  val
}: {
  productArps: ArpSegment[];
  header: string;
  val: number | Date;
  segmentTemplateName?: string;
  declineType?: string;
  cumulativeData?: CumulativeData;
}): ArpSegment[] {
  if (!arpsWasm) {
    throw new Error("arpsWasm is undefined - cannot update ratio decline segments");
  }

  if (productArps.length !== 2 || !productArps[0].isUserRatioDecline) {
    throw new Error("Invalid ratio decline segments");
  }

  const segment1 = { ...productArps[0] };
  const segment2 = { ...productArps[1] };

  // Handle field updates
  if (header === ArpFields.Q0) {
    // Update Q0 (starting ratio value)
    const newQ0 = val as number;
    // Guard against division by zero in WASM when Q0=0 (same pattern as ramp-up segments).
    // Store the guard value so the chart rate formula (rate = qi * f(t)) works near 0.
    // getRatioDisplayValues normalizes this back to 0 for the input field display.
    const q0ForCalc = newQ0 < EPSILON ? RATIO_ZERO_GUARD : newQ0;
    segment1.qi = q0ForCalc;

    // Recalculate Di with new Q0
    const days = daysDiff(segment1.startDate, segment1.endDate);
    segment1.di = arpsWasm.calculateDiAtDaysFromQiToQf(
      q0ForCalc,
      segment1.qf, // Qi (ending value) stays the same
      RATIO_DECLINE_B_FACTOR,
      days
    );
  } else if (header === ArpFields.Qi) {
    // Update Qi (ending ratio value)
    const newQi = val as number;
    // Guard Qi=0 so the flat segment after the decline plots at ~0 rather than disappearing
    const qiForCalc = newQi < EPSILON ? RATIO_ZERO_GUARD : newQi;
    segment1.qf = qiForCalc;
    segment2.qi = qiForCalc;
    segment2.qf = qiForCalc;

    // Recalculate Di with new Qi
    const days = daysDiff(segment1.startDate, segment1.endDate);
    // Guard against division by zero: use epsilon for Q0 if it was stored as the guard value
    const q0ForCalc = segment1.qi < EPSILON ? RATIO_ZERO_GUARD : segment1.qi;
    segment1.di = arpsWasm.calculateDiAtDaysFromQiToQf(
      q0ForCalc, // Q0 (starting value) stays the same
      qiForCalc,
      RATIO_DECLINE_B_FACTOR, // b (linear decline)
      days
    );
  } else if (header === ArpFields.Duration) {
    // Update Duration (months)
    const newDurationMonths = val as number;
    const startDate = new Date(segment1.startDate);
    const newEndDate = addAvgMonthsToDate(startDate, newDurationMonths).toDate();

    segment1.endDate = newEndDate.toISOString();
    segment2.startDate = newEndDate.toISOString();

    // Recalculate Di with new duration
    const days = daysDiff(segment1.startDate, segment1.endDate);
    // Guard against division by zero: use epsilon for Q0 if it was stored as the guard value
    const q0ForCalc = segment1.qi < EPSILON ? RATIO_ZERO_GUARD : segment1.qi;
    segment1.di = arpsWasm.calculateDiAtDaysFromQiToQf(
      q0ForCalc,
      segment1.qf,
      RATIO_DECLINE_B_FACTOR,
      days
    );
  }

  return [segment1, segment2];
}

/**
 * Sync ratio decline segment dates (start and end) with the primary product.
 * - Segment 1 start date = primary product first segment start date
 * - Segment 2 end date = primary product last valid segment end date
 * This should be called whenever the primary product's segments change.
 *
 * @param arps - The current forecast with all segments
 * @param primaryProductStartDate - The start date of the primary product's first segment
 * @param primaryProductEndDate - The end date of the primary product's last valid segment
 * @returns Updated arps with ratio segments synced to primary product dates
 */
export function syncRatioSegmentsWithPrimaryProduct(
  arps: ICheckedForecast,
  primaryProductStartDate: Date,
  primaryProductEndDate: Date
): ICheckedForecast {
  if (!primaryProductStartDate || !primaryProductEndDate || !arps.arps) {
    return arps;
  }

  const updatedArps = [...arps.arps];
  let hasChanges = false;

  const newStartDate = primaryProductStartDate.toISOString();
  const newEndDate = primaryProductEndDate.toISOString();

  // Find all ratio decline segments (they come in pairs: segment 1 decline, segment 2 flat)
  const ratioSegmentsByProduct = new Map<string, ArpSegment[]>();

  updatedArps.forEach((seg) => {
    if (seg.isUserRatioDecline) {
      if (!ratioSegmentsByProduct.has(seg.product)) {
        ratioSegmentsByProduct.set(seg.product, []);
      }
      ratioSegmentsByProduct.get(seg.product).push(seg);
    }
  });

  // Update each ratio product's segments:
  // - Segment 1 (decline): sync start date, update end date based on duration
  // - Segment 2 (flat): sync start date from segment 1 end, sync end date with primary
  ratioSegmentsByProduct.forEach((segments) => {
    if (segments.length === 2) {
      // Sort by segment index to ensure correct order
      const sortedSegments = segments.sort((a, b) => a.segmentIndex - b.segmentIndex);
      const segment1 = sortedSegments[0]; // Decline segment
      const segment2 = sortedSegments[1]; // Flat segment

      // Calculate segment 1's duration in months (preserve existing duration)
      const currentDurationMonths = avgMonthsDiff(segment1.startDate, segment1.endDate);

      // Calculate new segment 1 end date based on new start date + existing duration
      const newSeg1EndDate = addAvgMonthsToDate(
        primaryProductStartDate,
        currentDurationMonths
      ).toDate();
      const newSeg1EndDateISO = newSeg1EndDate.toISOString();

      // Update segment 1 if start or end date changed
      if (segment1.startDate !== newStartDate || segment1.endDate !== newSeg1EndDateISO) {
        const segment1Index = updatedArps.findIndex((seg) => seg === segment1);
        if (segment1Index >= 0) {
          updatedArps[segment1Index] = {
            ...segment1,
            startDate: newStartDate,
            endDate: newSeg1EndDateISO
          };
          hasChanges = true;
        }
      }

      // Update segment 2 if start or end date changed
      if (segment2.startDate !== newSeg1EndDateISO || segment2.endDate !== newEndDate) {
        const segment2Index = updatedArps.findIndex((seg) => seg === segment2);
        if (segment2Index >= 0) {
          updatedArps[segment2Index] = {
            ...segment2,
            startDate: newSeg1EndDateISO,
            endDate: newEndDate
          };
          hasChanges = true;
        }
      }
    }
  });

  if (!hasChanges) {
    return arps;
  }

  return {
    ...arps,
    arps: updatedArps
  };
}
